import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Trans, withI18nContext } from '@mc/i18n'
import { EmailFormatWarning } from '@mc/authentication'
import { AccountService } from '@mc/api'
import { Button } from '@mc/design-system'
import { usePrimaryCta } from '@mc/growth'
import { Locations, EventTypes, track, getPageSource } from '@mc/track-event'
import {
  EmailCaptureError,
  EmailCaptureForm,
  EmailCaptureInput,
  GdprCheckbox,
  setCartEmailCookie,
} from '@mc/email-capture'
import { paths } from '@mc/paths'

import { trackCtaClicked } from '../../lib/events'
import { EmailAlreadyRegisteredWarning } from './EmailAlreadyRegisteredWarning'
import { EMAIL_CAPTURE_PROMO_MODAL_I18N_NAMESPACE, i18nInstance } from './i18n'
import { setPromoModalEmailCaptureCookie } from './promoModalEmailCaptureCookie'
import styles from './EmailCapture.module.scss'

// Code based  on web/apps/checkout/src/pages/EmailCapturePage/components/EmailCapture/EmailCapture.tsx
export const PromoModalEmailCaptureComp = ({
  pageSource,
}: {
  pageSource: string
}) => {
  const {
    isLoading,
    isError,
    isSuccess,
    isEmailRegistered,
    onCheckEmail,
    email,
  } = useCheckEmail()

  const { ctaHref, ctaCopy } = usePrimaryCta()

  useEffect(() => {
    if (!isLoading && isSuccess && email) {
      window.location.href = ctaHref
    }
  }, [ctaHref, isLoading, isEmailRegistered, isSuccess, email])

  const onSubmit = (data: { email: string }) => {
    track(EventTypes.EMAIL_CAPTURED, {
      location: Locations.PROMOTION_MODAL,
      page_source: getPageSource(),
      email: data.email,
      type: 'email',
    })

    onCheckEmail(data)
  }

  return (
    <EmailCaptureForm onSubmit={onSubmit}>
      <EmailCaptureInput
        label='Enter your email to get started'
        className='mc-mb-5 mc-invert'
      />

      <EmailFormatWarning
        pageSource={getPageSource()}
        trackProps={{
          location: Locations.PROMOTION_MODAL,
        }}
      />

      <GdprCheckbox className={`mc-text--left mc-invert ${styles.checkbox}`} />

      {!isLoading && isError && <EmailCaptureError className='mc-mb-2' />}
      {isEmailRegistered && <EmailAlreadyRegisteredWarning />}

      <Button
        fullWidth
        className='mc-mb-3'
        onClick={() => {
          trackCtaClicked({
            action: ctaCopy,
            location: Locations.PROMOTION_MODAL,
            pageSource,
            href: ctaHref,
          })
        }}
        type='submit'
        loading={isLoading}
      >
        {ctaCopy}
      </Button>

      <LegalEmailCapture />
    </EmailCaptureForm>
  )
}

export const PromoModalEmailCapture = withI18nContext(i18nInstance)(
  PromoModalEmailCaptureComp,
)

// copied from web/apps/checkout/src/pages/EmailCapturePage/hooks/useCheckEmail.ts
// but removed onSettled logic of setting the fire_email_captured cookie
// but I cannot call it - circular dependency
// if we productize this exp we could move that logic to ui/growth
const EMAIL_DOES_NOT_EXIST = 404

export const useCheckEmail = () => {
  const [email, setEmail] = useState('')
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isEmailRegistered, setIsEmailRegistered] = useState(false)

  const { isFetching } = useQuery(
    ['checkEmail', email],
    () => AccountService.getRegisteredEmail({ email }),
    {
      enabled: !!email,
      onSuccess: () => {
        // User already exists -- cannot complete flow, they need to sign in.
        setIsEmailRegistered(true)
        setCartEmailCookie(email)
      },
      onError: ({ status }) => {
        // User does not exist -- continue flow
        if (status === EMAIL_DOES_NOT_EXIST) {
          setIsSuccess(true)
          setCartEmailCookie(email)
          setPromoModalEmailCaptureCookie() // in case we have set the email cookie somewhere else
        } else {
          setIsError(true)
        }
      },
    },
  )

  const onCheckEmail = (data: { email: string }) => setEmail(data.email)

  return {
    isLoading: isFetching,
    isError,
    isSuccess,
    isEmailRegistered,
    onCheckEmail,
    email,
  }
}

const LegalEmailCapture = () => (
  <Trans i18nKey='legal.regular' ns={EMAIL_CAPTURE_PROMO_MODAL_I18N_NAMESPACE}>
    {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
    <a
      className='mc-text--link'
      href={paths.terms}
      target='_blank'
      rel='noreferrer'
    />
    {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
    <a
      className='mc-text--link'
      href={paths.privacy}
      target='_blank'
      rel='noreferrer'
    />
  </Trans>
)
